import type { ExpandedState } from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'

import {
  getStemsFromTracksArray,
  getVersionsFromTracksArray,
} from '@disco/utils/nesting'

import type { Track } from 'types/track'

import type { WithId } from 'components/ds/DataTable/types'

import { useSelector } from 'hooks/use-redux'

export type NestedTrackRow = Track &
  Pick<WithId, 'isNestingGroupHeader' | 'nestingGroupLabel'>

export type SearchTrackRow = Track & { subTracks?: NestedTrackRow[] }

// Only the marker fields are real here - the cast is contained to this factory.
const makeSectionRow = (
  parentId: Track['id'],
  label: 'Versions' | 'Stems',
): NestedTrackRow =>
  ({
    id: `${parentId}-${label.toLowerCase()}-header`,
    isNestingGroupHeader: true,
    nestingGroupLabel: label,
  } as unknown as NestedTrackRow)

const section = (
  parentId: Track['id'],
  label: 'Versions' | 'Stems',
  tracks: Track[],
): NestedTrackRow[] =>
  tracks.length ? [makeSectionRow(parentId, label), ...tracks] : []

const getSubRows = (row: SearchTrackRow) => row.subTracks

const isExpandedRow = (expanded: ExpandedState, rowId: string) =>
  expanded === true || Boolean(expanded[rowId])

/** Expansion lives here, not in DataTable: selection and the playback
 * tracklist both need the resulting row order. */
export const useNestedTrackRows = ({
  results,
  searchId,
  enabled,
}: {
  results: Track[]
  searchId?: string
  enabled: boolean
}) => {
  // Read the store directly: importing selectors/nesting from the view layer
  // trips a require cycle through the tracks / groups / search selectors.
  const tracksById = useSelector(state => state.tracks)
  const [expanded, setExpanded] = useState<ExpandedState>({})

  const tracksByParent = useMemo(() => {
    const byParent = new Map<number, Track[]>()
    if (!enabled) return byParent
    Object.values<Track>(tracksById ?? {}).forEach(track => {
      const parentId = track?.nesting_parent_id
      if (parentId == null) return
      const siblings = byParent.get(parentId)
      if (siblings) siblings.push(track)
      else byParent.set(parentId, [track])
    })
    return byParent
  }, [enabled, tracksById])

  useEffect(() => {
    setExpanded({})
  }, [searchId])

  const rows = useMemo<SearchTrackRow[]>(() => {
    if (!enabled) return results

    const groupedRows = results.map<SearchTrackRow>(track => {
      const nested = tracksByParent.get(Number(track.id))
      if (!nested?.length) return track

      // Children carry the parent's scope so MDS selectability matches the
      // row they sit under - the refresh endpoint doesn't set it.
      const withParentScope = (child: Track): Track =>
        child.scope ? child : { ...child, scope: track.scope }

      const subTracks = [
        ...section(
          track.id,
          'Versions',
          getVersionsFromTracksArray(nested).map(withParentScope),
        ),
        ...section(
          track.id,
          'Stems',
          getStemsFromTracksArray(nested).map(withParentScope),
        ),
      ]

      return subTracks.length ? { ...track, subTracks } : track
    })

    // Hide duplicate search hits even while their parent is collapsed. Keep
    // child-only matches when their parent isn't part of these results.
    const nestedIds = new Set(
      groupedRows.flatMap(row =>
        (row.subTracks ?? [])
          .filter(child => !child.isNestingGroupHeader)
          .map(child => child.id),
      ),
    )
    return groupedRows.filter(row => !nestedIds.has(row.id))
  }, [enabled, results, tracksByParent])

  /** What the table renders, minus section labels. */
  const visibleRows = useMemo<Track[]>(
    () =>
      rows.flatMap(row =>
        row.subTracks && isExpandedRow(expanded, String(row.id))
          ? [row, ...row.subTracks.filter(c => !c.isNestingGroupHeader)]
          : [row],
      ),
    [rows, expanded],
  )

  return {
    rows,
    visibleRows,
    expanded,
    onExpandedChange: setExpanded,
    getSubRows: enabled ? getSubRows : undefined,
  }
}
